import React, { useState } from "react";
import { CheckSquare, ChevronDown, Square } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { ItemUI } from "../types/item";
import { User } from "../types/user";
import SplitControlsModal from "../components/SplitControlsModal";
import SplitPie from "../components/SplitPie";
import ItemRowShell from "./ItemRowShell";

interface SplitItemRowProps {
  item: ItemUI;
  selectedUser: User | null;
  usersById: Map<string, User>;
  // whether the selected user's shares can be edited (true whenever a mate
  // is selected)
  canEditShares: boolean;
  // the split count (denominator) — anyone can change it
  canEditSplits: boolean;
  onSetShare: (shares: number) => void;
  onSetSplits: (splits: number) => void;
  onQuickClaim: (claim: boolean) => void;
  // multi-select for the bulk "split all" bar
  selected: boolean;
  onToggleSelect: () => void;
}

// An item on the shared check (/:id): the claim box on the right.
const SplitItemRow: React.FC<SplitItemRowProps> = ({
  item,
  selectedUser,
  usersById,
  canEditShares,
  canEditSplits,
  onSetShare,
  onSetSplits,
  onQuickClaim,
  selected,
  onToggleSelect,
}) => {
  // the caret opens this item's split controls (steppers) in a modal
  const [showControls, setShowControls] = useState(false);

  const claimed = Object.values(item.shares).reduce((a, b) => a + b, 0);
  const remaining = Math.max(0, item.splits - claimed);
  const myShares = selectedUser ? (item.shares[selectedUser.id] ?? 0) : 0;
  // I can take up to whatever's left, plus what I already hold
  const myMax = myShares + remaining;

  // one tap on the box grabs/releases a portion for the selected user: grey
  // "tap to claim" when you're not on it, white "tap to unclaim" when you are.
  // The caret opens the steppers in a modal for precise control (as does a
  // tap on the box when there's nothing to claim).
  const claimedByMe = myShares > 0;
  // "tap to claim" only while there's an open portion; once others hold the
  // whole item the box is just the donut (tap opens the split controls)
  const quickAvailable =
    canEditShares && selectedUser != null && (claimedByMe || remaining > 0);

  const handleSummaryClick = () => {
    if (quickAvailable) onQuickClaim(!claimedByMe);
    else setShowControls(true);
  };

  return (
    <ItemRowShell
      item={item}
      variant="split"
      leading={
        // check items to set their split counts together (bar above the
        // bottom bar)
        selected ? (
          <CheckSquare
            className="item-select checked"
            onClick={onToggleSelect}
          />
        ) : (
          <Square className="item-select" onClick={onToggleSelect} />
        )
      }
      overlays={
        // lets the modal play its close animation
        <AnimatePresence>
          {showControls && (
            <SplitControlsModal
              key="controls"
              item={item}
              selectedUser={selectedUser}
              usersById={usersById}
              myShares={myShares}
              myMax={myMax}
              claimed={claimed}
              canEditShares={canEditShares}
              canEditSplits={canEditSplits}
              onSetShare={onSetShare}
              onSetSplits={onSetSplits}
              handleClose={() => setShowControls(false)}
            />
          )}
        </AnimatePresence>
      }
    >
      <div className="item-actions">
        <button
          type="button"
          className={`split-summary${
            quickAvailable ? (claimedByMe ? " claimed" : " needs-claim") : ""
          }`}
          onClick={handleSummaryClick}
        >
          {quickAvailable && (
            // always two lines: "tap to" / "(un)claim"
            <span className="claim-hint">
              tap to
              <br />
              {claimedByMe ? "unclaim" : "claim"}
            </span>
          )}
          <span className="split-visual">
            <SplitPie splits={item.splits} claimed={claimed} mine={myShares} />
          </span>
        </button>
        <ChevronDown
          size={16}
          className="split-caret"
          onClick={(e) => {
            e.stopPropagation();
            setShowControls(true);
          }}
        />
      </div>
    </ItemRowShell>
  );
};

export default SplitItemRow;
